import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  CreditCard, CheckCircle, XCircle, Clock, User, 
  Calendar, DollarSign, AlertCircle, RefreshCw,
  Search, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

const AdminPayments = () => {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingPayment, setRejectingPayment] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchPayments();
  }, [statusFilter]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      // Fetch pending payments
      const pendingRes = await axios.get('/membership/admin/pending-payments');
      setPendingPayments(pendingRes.data.pending_payments || []);
      
      // Fetch all payment requests for history
      const allRes = await axios.get('/membership/admin/all-payment-requests');
      setAllPayments(allRes.data.requests || []);
    } catch (error) {
      console.error('Payments fetch error:', error);
      // If all-payment-requests doesn't exist, just use pending
      setAllPayments(pendingPayments);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (paymentId) => {
    setProcessingId(paymentId);
    try {
      const response = await axios.post(`/membership/admin/approve-payment/${paymentId}`);
      toast.success(response.data.message || 'Ödeme onaylandı!');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onaylama başarısız');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectingPayment) return;
    
    setProcessingId(rejectingPayment.id);
    try {
      const response = await axios.post(
        `/membership/admin/reject-payment/${rejectingPayment.id}?reason=${encodeURIComponent(rejectReason || 'Ödeme doğrulanamadı')}`
      );
      toast.success(response.data.message || 'Ödeme reddedildi');
      setRejectDialogOpen(false);
      setRejectingPayment(null);
      setRejectReason('');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reddetme başarısız');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectDialog = (payment) => {
    setRejectingPayment(payment);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/40"><Clock className="h-3 w-3 mr-1" /> Bekliyor</Badge>;
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/40"><CheckCircle className="h-3 w-3 mr-1" /> Onaylandı</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/40"><XCircle className="h-3 w-3 mr-1" /> Reddedildi</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredPayments = statusFilter === 'pending' 
    ? pendingPayments 
    : allPayments.filter(p => statusFilter === 'all' || p.status === statusFilter);

  const searchedPayments = filteredPayments.filter(p => 
    !searchQuery || 
    p.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.transaction_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.reference_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-primary" />
          Ödeme Yönetimi
        </h1>
        <p className="text-slate-400 mt-1">Bekleyen ve işlenmiş ödeme taleplerini yönetin</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-border/40 bg-yellow-900/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Bekleyen</p>
                <p className="text-2xl font-bold text-yellow-400">{pendingPayments.length}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-green-900/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Onaylanan</p>
                <p className="text-2xl font-bold text-green-400">
                  {allPayments.filter(p => p.status === 'approved').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-red-900/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Reddedilen</p>
                <p className="text-2xl font-bold text-red-400">
                  {allPayments.filter(p => p.status === 'rejected').length}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-primary/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Toplam Gelir</p>
                <p className="text-2xl font-bold text-primary">
                  ₺{allPayments.filter(p => p.status === 'approved').reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-border/40 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                placeholder="E-posta, isim veya referans kodu ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-brand-dark border-border/40"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant={statusFilter === 'pending' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('pending')}
                className={statusFilter === 'pending' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
              >
                <Clock className="h-4 w-4 mr-1" /> Bekleyen
              </Button>
              <Button 
                variant={statusFilter === 'approved' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('approved')}
                className={statusFilter === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                <CheckCircle className="h-4 w-4 mr-1" /> Onaylanan
              </Button>
              <Button 
                variant={statusFilter === 'rejected' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('rejected')}
                className={statusFilter === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                <XCircle className="h-4 w-4 mr-1" /> Reddedilen
              </Button>
              <Button 
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('all')}
              >
                Tümü
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={fetchPayments}>
              <RefreshCw className="h-4 w-4 mr-1" /> Yenile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payments List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-500" />
          <p className="text-slate-400 mt-2">Yükleniyor...</p>
        </div>
      ) : searchedPayments.length === 0 ? (
        <Card className="border-border/40">
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-lg text-slate-400">
              {statusFilter === 'pending' ? 'Bekleyen ödeme talebi yok' : 'Ödeme talebi bulunamadı'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {searchedPayments.map((payment) => (
            <Card key={payment.id} className="border-border/40 hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* User Info */}
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{payment.user_name || 'İsimsiz'}</p>
                        {getStatusBadge(payment.status)}
                      </div>
                      <p className="text-sm text-slate-400">{payment.user_email}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(payment.created_at)}
                        </span>
                        {(payment.reference_code || payment.transaction_id) && (
                          <span className="flex items-center gap-1">
                            Referans: <code className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded font-bold">{payment.reference_code || payment.transaction_id}</code>
                          </span>
                        )}
                      </div>
                      {payment.note && (
                        <p className="text-xs text-slate-400 mt-1 italic">Not: {payment.note}</p>
                      )}
                      {payment.rejection_reason && (
                        <p className="text-xs text-red-400 mt-1">Red sebebi: {payment.rejection_reason}</p>
                      )}
                    </div>
                  </div>

                  {/* Plan & Price */}
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <Badge className="bg-primary/20 text-primary border-primary/40 mb-1">
                        {payment.plan_type?.toUpperCase() || 'PRO'}
                      </Badge>
                      <p className="text-xs text-slate-500">
                        {payment.billing_cycle === 'yearly' ? 'Yıllık' : 'Aylık'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">₺{payment.amount?.toLocaleString()}</p>
                    </div>

                    {/* Actions */}
                    {payment.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(payment.id)}
                          disabled={processingId === payment.id}
                        >
                          {processingId === payment.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Onayla
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-900/20"
                          onClick={() => openRejectDialog(payment)}
                          disabled={processingId === payment.id}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reddet
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-brand-card border-border/40">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <XCircle className="h-5 w-5" />
              Ödeme Talebini Reddet
            </DialogTitle>
            <DialogDescription>
              {rejectingPayment?.user_email} kullanıcısının ödeme talebi reddedilecek.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm text-slate-400 mb-2 block">Red Sebebi</label>
            <Textarea
              placeholder="Ödeme doğrulanamadı, yanlış tutar vb..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="bg-brand-dark border-border/40"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              İptal
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleReject}
              disabled={processingId === rejectingPayment?.id}
            >
              {processingId === rejectingPayment?.id ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <XCircle className="h-4 w-4 mr-1" />
              )}
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayments;
